import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HardDrive, FolderOpen, Search, RefreshCw, Unplug,
  Loader2, Image, Play, Download,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useThemeStore } from '../../store/themeStore';
import { cloudStorageAPI, getErrorMessage } from '../../services/api';
import type { CloudFile, CloudAccount, UrlMedia } from './types';

interface Props {
  setUrlMediaItems: React.Dispatch<React.SetStateAction<UrlMedia[]>>;
}

export const CloudStorageTab: React.FC<Props> = ({ setUrlMediaItems }) => {
  const { theme } = useThemeStore();
  const { t } = useTranslation();
  const [cloudAccounts, setCloudAccounts] = useState<CloudAccount[]>([]);
  const [activeCloudAccount, setActiveCloudAccount] = useState<string | null>(null);
  const [cloudFiles, setCloudFiles] = useState<CloudFile[]>([]);
  const [cloudSearchQuery, setCloudSearchQuery] = useState('');
  const [isCloudLoading, setIsCloudLoading] = useState(false);
  const [cloudNextPage, setCloudNextPage] = useState<string | null>(null);
  const [cloudImporting, setCloudImporting] = useState<Record<string, number>>({});
  const [isConnecting, setIsConnecting] = useState(false);

  // Load accounts on mount + listen for OAuth callback
  useEffect(() => {
    loadCloudAccounts();

    const handleMessage = (event: MessageEvent) => {
      // Validate origin to prevent cross-site message injection
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'cloud-storage-connected') {
        setIsConnecting(false);
        loadCloudAccounts();
        const providerName = event.data.provider === 'google_drive' ? t('compose_google_drive', 'Google Drive') : t('compose_dropbox', 'Dropbox');
        toast.success(t('compose_provider_connected', '{{provider}} connected!', { provider: providerName }));
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCloudAccounts = async () => {
    try {
      const resp = await cloudStorageAPI.listAccounts();
      setCloudAccounts(resp.data.accounts || []);
    } catch {
      // silently fail on initial load
    }
  };

  const connectCloudProvider = async (provider: string) => {
    if (isConnecting) return;
    setIsConnecting(true);
    try {
      const resp = await cloudStorageAPI.startOAuth(provider);
      window.open(resp.data.auth_url, '_blank', 'width=600,height=700');
      // isConnecting is cleared when postMessage arrives, or after timeout
      setTimeout(() => setIsConnecting(false), 60_000);
    } catch (err: unknown) {
      setIsConnecting(false);
      toast.error(getErrorMessage(err, t('compose_failed_start_connection', 'Failed to start connection')));
    }
  };

  const browseCloudFiles = async (
    accountId: string,
    searchQuery?: string,
    pageToken?: string | null,
  ) => {
    setIsCloudLoading(true);
    setActiveCloudAccount(accountId);
    try {
      const resp = await cloudStorageAPI.browse(accountId, {
        query: searchQuery || undefined,
        page_token: pageToken || undefined,
      });
      const newFiles = resp.data.files || [];
      // Append when paginating, replace when new search
      if (pageToken) {
        setCloudFiles((prev) => [...prev, ...newFiles]);
      } else {
        setCloudFiles(newFiles);
      }
      setCloudNextPage(resp.data.next_page_token || null);
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, t('compose_failed_browse_files', 'Failed to browse files')));
    } finally {
      setIsCloudLoading(false);
    }
  };

  const importCloudFile = async (accountId: string, file: CloudFile) => {
    const fileKey = file.id;
    setCloudImporting((prev) => ({ ...prev, [fileKey]: 0 }));

    try {
      const result = await cloudStorageAPI.importFile(accountId, file.id, (loaded, total) => {
        const pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
        setCloudImporting((prev) => ({ ...prev, [fileKey]: pct }));
      });

      const data = result.data;
      setUrlMediaItems([
        {
          id: Math.random().toString(36).substring(7),
          url: file.name,
          filename: file.name,
          contentType: file.mime_type,
          type: file.type,
          status: 'done',
          progress: 100,
          mediaId: data.media_id,
        },
      ]);
      toast.success(t('compose_imported_file', 'Imported {{name}}', { name: file.name }));
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, t('compose_failed_import_file', 'Failed to import {{name}}', { name: file.name })));
    } finally {
      setCloudImporting((prev) => {
        const next = { ...prev };
        delete next[fileKey];
        return next;
      });
    }
  };

  const disconnectCloudAccount = async (accountId: string) => {
    try {
      await cloudStorageAPI.disconnect(accountId);
      setCloudAccounts((prev) => prev.filter((a) => a.id !== accountId));
      if (activeCloudAccount === accountId) {
        setActiveCloudAccount(null);
        setCloudFiles([]);
      }
      toast.success(t('compose_account_disconnected', 'Account disconnected'));
    } catch {
      toast.error(t('compose_failed_disconnect', 'Failed to disconnect account'));
    }
  };

  return (
    <div className="space-y-4">
      {/* Connected Accounts */}
      {cloudAccounts.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500">{t('compose_connected_accounts', 'Connected Accounts')}</p>
          {cloudAccounts.map((acc) => (
            <div key={acc.id} className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-100">
              <HardDrive className="h-4 w-4 text-gray-400" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-700 truncate">{acc.display_name}</p>
                <p className="text-[10px] text-gray-400">
                  {acc.provider === 'google_drive' ? t('compose_google_drive', 'Google Drive') : t('compose_dropbox', 'Dropbox')} — {acc.email}
                </p>
              </div>
              <button
                onClick={() => browseCloudFiles(acc.id)}
                className="text-xs font-medium px-2.5 py-1.5 rounded-md text-white"
                style={{ backgroundColor: theme.hex.primary }}
              >
                <FolderOpen className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => disconnectCloudAccount(acc.id)}
                className="text-xs text-gray-400 hover:text-red-500 p-1.5 rounded"
              >
                <Unplug className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Connect Buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => connectCloudProvider('google_drive')}
          disabled={isConnecting}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isConnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span>🔵</span>}
          {t('compose_connect_google_drive', 'Connect Google Drive')}
        </button>
        <button
          onClick={() => connectCloudProvider('dropbox')}
          disabled={isConnecting}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isConnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span>📦</span>}
          {t('compose_connect_dropbox', 'Connect Dropbox')}
        </button>
      </div>

      {/* File Browser */}
      {activeCloudAccount && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* Search */}
          <div className="p-2.5 border-b border-gray-100 bg-gray-50">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  value={cloudSearchQuery}
                  onChange={(e) => setCloudSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && activeCloudAccount) {
                      setCloudNextPage(null); // reset pagination on new search
                      browseCloudFiles(activeCloudAccount, cloudSearchQuery);
                    }
                  }}
                  placeholder={t('compose_search_media', 'Search media files...')}
                  className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-md text-xs bg-white focus:ring-1"
                  style={{ outlineColor: theme.hex.primaryRing }}
                />
              </div>
              <button
                onClick={() => {
                  setCloudNextPage(null);
                  browseCloudFiles(activeCloudAccount, cloudSearchQuery);
                }}
                disabled={isCloudLoading}
                className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                {isCloudLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* File Grid */}
          <div className="max-h-64 overflow-y-auto p-2.5">
            {isCloudLoading && cloudFiles.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
              </div>
            ) : cloudFiles.length === 0 ? (
              <div className="text-center py-8">
                <Image className="mx-auto h-8 w-8 text-gray-200 mb-2" />
                <p className="text-xs text-gray-400">{t('compose_no_media_files', 'No media files found')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {cloudFiles.map((file) => {
                  const isImporting = file.id in cloudImporting;
                  return (
                    <button
                      key={file.id}
                      onClick={() => {
                        if (!isImporting && activeCloudAccount) importCloudFile(activeCloudAccount, file);
                      }}
                      disabled={isImporting}
                      className="relative group text-left border border-gray-100 rounded-lg overflow-hidden hover:border-gray-300 transition-all"
                    >
                      {file.thumbnail ? (
                        <img src={file.thumbnail} alt={file.name} className="w-full h-20 object-cover" />
                      ) : (
                        <div className="w-full h-20 bg-gray-100 flex items-center justify-center">
                          {file.type === 'video' ? <Play className="h-6 w-6 text-gray-300" /> : <Image className="h-6 w-6 text-gray-300" />}
                        </div>
                      )}
                      <div className="p-1.5">
                        <p className="text-[10px] text-gray-600 truncate">{file.name}</p>
                      </div>
                      {isImporting && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                          <div className="text-center">
                            <Loader2 className="h-5 w-5 animate-spin text-white mx-auto" />
                            <p className="text-[10px] text-white mt-1">{cloudImporting[file.id]}%</p>
                          </div>
                        </div>
                      )}
                      {!isImporting && (
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center rounded-lg">
                          <Download className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Load More — passes page_token for proper pagination */}
          {cloudNextPage && (
            <div className="p-2 border-t border-gray-100 text-center">
              <button
                onClick={() => {
                  if (activeCloudAccount) browseCloudFiles(activeCloudAccount, cloudSearchQuery, cloudNextPage);
                }}
                disabled={isCloudLoading}
                className="text-xs font-medium hover:underline disabled:opacity-50"
                style={{ color: theme.hex.primaryText }}
              >
                {isCloudLoading ? t('compose_loading', 'Loading...') : t('compose_load_more_files', 'Load more files')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
